import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const {
  FiUsers,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiX,
  FiCheck,
  FiUser,
  FiPhone,
  FiMail,
  FiShield,
  FiToggleLeft,
  FiToggleRight,
  FiRefreshCw
} = FiIcons;

const ManageOperators = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredOperators, setFilteredOperators] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    operator_level: 'L1',
    status: 'Active'
  });

  useEffect(() => {
    fetchOperators();
  }, []);

  useEffect(() => {
    filterOperators();
  }, [operators, searchQuery, levelFilter]);

  const fetchOperators = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('operators_a7b5c9') // Updated table name
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setOperators(data || []);
    } catch (error) {
      console.error('Error fetching operators:', error);
      toast.error('Failed to load operators');
    } finally {
      setLoading(false);
    }
  };

  const filterOperators = () => {
    let filtered = [...operators];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(operator =>
        operator.name?.toLowerCase().includes(query) ||
        operator.phone?.includes(query) ||
        operator.email?.toLowerCase().includes(query)
      );
    }

    // Apply level filter
    if (levelFilter !== 'all') {
      filtered = filtered.filter(operator => operator.operator_level === levelFilter);
    }

    setFilteredOperators(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return false;
    }
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error('Phone number must be 10 digits');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.operator_level) {
      toast.error('Operator level is required');
      return false;
    }

    // Check for duplicate email (excluding current operator if editing)
    const existingOperator = operators.find(op => 
      op.email === formData.email && op.id !== editingOperator?.id
    );
    if (existingOperator) {
      toast.error('Email already exists');
      return false;
    }

    return true;
  };

  const handleAddOperator = () => {
    setEditingOperator(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      operator_level: 'L1',
      status: 'Active'
    });
    setShowAddModal(true);
  };

  const handleEditOperator = (operator) => {
    setEditingOperator(operator);
    setFormData({
      name: operator.full_name || operator.name,
      phone: operator.phone,
      email: operator.email,
      operator_level: operator.operator_level,
      status: operator.status ? operator.status.charAt(0).toUpperCase() + operator.status.slice(1).toLowerCase() : 'Active'
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const operatorData = {
        full_name: formData.name,
        phone: formData.phone,
        email: formData.email,
        operator_level: formData.operator_level,
        status: formData.status.toLowerCase(),
        updated_at: new Date().toISOString()
      };

      if (editingOperator) {
        console.log('Updating operator with data:', operatorData);
        
        const { data, error } = await supabase
          .from('operators_a7b5c9')
          .update(operatorData)
          .eq('id', editingOperator.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating operator:', error);
          throw error;
        }
        
        console.log('Updated operator data:', data);
        
        // Update the operators array with the returned data
        setOperators(prev => prev.map(op => 
          op.id === editingOperator.id ? data : op
        ));
        
        toast.success('Operator updated successfully');
      } else {
        console.log('Creating new operator with data:', operatorData);
        
        const { data, error } = await supabase
          .from('operators_a7b5c9')
          .insert({
            user_id: `user_${Date.now()}`, // Generate a unique user_id
            ...operatorData,
            wallet_balance: 0,
            kyc_submitted: false,
            kyc_verified: false,
            completed_orders: 0,
            pending_orders: 0,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating operator:', error);
          throw error;
        }
        
        console.log('Created operator data:', data);
        
        // Add the new operator to the array
        setOperators(prev => [...prev, data]);
        
        toast.success('Operator added successfully');
      }
      
      setShowAddModal(false);
      
      // Reset form data
      setFormData({
        name: '',
        phone: '',
        email: '',
        operator_level: 'L1',
        status: 'Active'
      });
      setEditingOperator(null);
      
    } catch (error) {
      console.error('Error saving operator:', error);
      toast.error(`Failed to save operator: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteOperator = async (operatorId) => {
    if (window.confirm('Are you sure you want to delete this operator? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('operators_a7b5c9')
          .delete()
          .eq('id', operatorId);

        if (error) throw error;
        
        fetchOperators(); // Refresh the list
        toast.success('Operator deleted successfully');
      } catch (error) {
        console.error('Error deleting operator:', error);
        toast.error('Failed to delete operator');
      }
    }
  };

  const handleToggleStatus = async (operatorId) => {
    try {
      const operator = operators.find(op => op.id === operatorId);
      const currentStatus = operator?.status || 'active';
      const newStatus = currentStatus.toLowerCase() === 'active' ? 'inactive' : 'active';

      // Optimistically update the UI first
      setOperators(prev => prev.map(op => 
        op.id === operatorId ? { ...op, status: newStatus } : op
      ));

      const { error } = await supabase
        .from('operators_a7b5c9')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', operatorId);

      if (error) {
        // Revert the optimistic update on error
        setOperators(prev => prev.map(op => 
          op.id === operatorId ? { ...op, status: currentStatus } : op
        ));
        throw error;
      }
      
      toast.success(`Operator ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating operator status:', error);
      toast.error('Failed to update operator status. Please try again.');
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'L1': return 'bg-blue-100 text-blue-800';
      case 'L2': return 'bg-green-100 text-green-800';
      case 'L3': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    return (status || '').toLowerCase() === 'active'
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Operators</h1>
              <p className="text-gray-600 mt-1">
                Manage operator accounts and permissions
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={fetchOperators}
                className="flex items-center"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleAddOperator}
                className="flex items-center"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                Add Operator
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Operators</p>
                <p className="text-2xl font-semibold text-gray-900">{operators.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Operators</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {operators.filter(op => (op.status || '').toLowerCase() === 'active').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-full">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">L3 Operators</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {operators.filter(op => op.operator_level === 'L3').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-full">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {operators.reduce((sum, op) => sum + op.completed_orders, 0)}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
      {/* Filters and Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by name, phone, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {/* Level Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operator Level</label>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Levels</option>
                <option value="L1">L1</option>
                <option value="L2">L2</option>
                <option value="L3">L3</option>
              </select>
            </div>
            {/* Results Count */}
            <div className="flex items-end">
              <p className="text-sm text-gray-600">
                Showing {filteredOperators.length} of {operators.length} operators
              </p>
            </div>
          </div>
        </Card>
      </div>
      {/* Operators Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" />
            </div>
          ) : filteredOperators.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <SafeIcon icon={FiUsers} className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium">No operators found</h3>
              <p className="text-sm">
                Try adjusting your search or filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Level
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orders
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOperators.map((operator) => (
                    <tr key={operator.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-sm">
                            {operator.name?.charAt(0)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{operator.name}</div>
                            <div className="text-sm text-gray-500">{formatDate(operator.created_at)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2 text-gray-400" />
                          {operator.phone}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <SafeIcon icon={FiMail} className="w-4 h-4 mr-2 text-gray-400" />
                          {operator.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getLevelColor(operator.operator_level)}`}>
                          {operator.operator_level}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(operator.status)}`}>
                          {operator.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="font-medium">Completed: {operator.completed_orders || 0}</div>
                        <div>Pending: {operator.pending_orders || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="icon"
                            size="sm"
                            onClick={() => handleEditOperator(operator)}
                          >
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleStatus(operator.id)}
                            className={`flex items-center ${
                              (operator.status || '').toLowerCase() === 'active' 
                                ? 'text-red-600 border-red-300 hover:bg-red-50' 
                                : 'text-green-600 border-green-300 hover:bg-green-50'
                            }`}
                          >
                            <SafeIcon icon={(operator.status || '').toLowerCase() === 'active' ? FiToggleLeft : FiToggleRight} className="w-4 h-4 mr-1" />
                            <span className="text-xs">
                              {(operator.status || '').toLowerCase() === 'active' ? 'Deactivate' : 'Activate'}
                            </span>
                          </Button>
                          <Button
                            variant="icon"
                            size="sm"
                            onClick={() => handleDeleteOperator(operator.id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="relative p-8 bg-white rounded-lg shadow-xl w-full max-w-lg mx-auto"
          >
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingOperator ? 'Edit Operator' : 'Add New Operator'}
              </h3>
              <Button
                variant="icon"
                onClick={() => setShowAddModal(false)}
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Operator Level
                  </label>
                  <select
                    name="operator_level"
                    value={formData.operator_level}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="L1">L1 - Junior</option>
                    <option value="L2">L2 - Intermediate</option>
                    <option value="L3">L3 - Senior</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingOperator ? 'Update Operator' : 'Add Operator'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ManageOperators;